import { put, select } from "redux-saga/effects";
import {
  AvailableProduct,
  WalletOffersResponse,
  WalletSummaryResponse,
  AvailableProductEnum,
  MerchantOfRecord,
  ProductClassEnum,
  PaymentSchedule,
} from "redmond";
import { IStoreState } from "../../../../reducers/types";
import {
  setBestOfferOverall,
  setFetchApplicableTravelWalletItemsCallStateFailure,
  setFetchApplicableTravelWalletItemsCallStateSuccess,
  setTravelWalletCredit,
  setTravelWalletOffers,
} from "../../actions/actions";
import { fetchTravelWalletOffers } from "../../../../api/v0/travel-wallet/fetchTravelWalletOffers";
import { fetchTravelWalletSummary } from "../../../../api/v0/travel-wallet/fetchTravelWalletSummary";
import { getVacationRentalShopSelectedListing } from "../../../shop/reducer";
import { getFromDate, getUntilDate } from "../../../search/reducer";
import dayjs from "dayjs";
import { getVacationRentalsPriceQuote } from "../../reducer";
import { actions as walletActions } from "../../../travel-wallet/actions";

export function* fetchApplicableTravelWalletItemsSaga() {
  try {
    const state: IStoreState = yield select();

    const selectedListing = getVacationRentalShopSelectedListing(state);

    if (!selectedListing) {
      throw new Error("Must have selected listing");
    }

    const priceQuotePricing = getVacationRentalsPriceQuote(state);

    const fromDate = getFromDate(state);
    const untilDate = getUntilDate(state);

    priceQuotePricing?.rate.detailedPrice;

    const offersResponse: WalletOffersResponse = yield fetchTravelWalletOffers({
      products: [
        {
          AvailableProduct: AvailableProductEnum.Home,
          listingId: selectedListing.listing.id.id,
          merchant: MerchantOfRecord.Hopper,
          market: selectedListing.listing.market?.id,
          stayDates: {
            from: dayjs(fromDate).format("YYYY-MM-DD"),
            until: dayjs(untilDate).format("YYYY-MM-DD"),
          },
          belongsToPackage: false,
          productPricing: {
            productClass: ProductClassEnum.Economy,
            currency:
              priceQuotePricing?.rate.detailedPrice.payNowTotal.fiat
                .currencyCode ??
              selectedListing.availability.rate?.price.total.fiat
                .currencyCode ??
              "USD",
            basePrice:
              priceQuotePricing?.rate.detailedPrice.sellPrice.fiat.value ??
              selectedListing.availability.rate?.price.rentTotal.fiat.value ??
              0,
            grossBookValue:
              priceQuotePricing?.rate.detailedPrice.payNowTotal.fiat.value ??
              selectedListing.availability.rate?.price.total.fiat.value ??
              0,
            ancillaryTotal: 0, // hardcoded bc Tysons does this https://github.com/hopper-org/Tysons/blob/1167df0bdaa8ae2a5255ea0203438deacff071ae/server/src/main/scala/com/hopper/tysons/service/hotels/availability/converter/ImpFastAvailabilityResponseConverter.scala#L552
            // cap1 does not have discounts so can use total price
            preDiscountTotal:
              priceQuotePricing?.rate.detailedPrice.payNowTotal.fiat.value ??
              selectedListing.availability.rate?.price.total.fiat.value ??
              0,
            preDiscountGrandTotal:
              priceQuotePricing?.rate.detailedPrice.payNowTotal.fiat.value ??
              selectedListing.availability.rate?.price.total.fiat.value ??
              0,
            sellPrice:
              priceQuotePricing?.rate.detailedPrice.sellPrice.fiat.value ??
              selectedListing.availability.rate?.price.total.fiat.value ??
              0,
          },
          paymentSchedule: PaymentSchedule.PayNow,
          isHostFunded: false,
          hostId: selectedListing?.listing.privateHostInfo?.id,
          collectionType: selectedListing.listing.listingCollection,
        } as AvailableProduct,
      ],
    });

    const totalFiatValue =
      priceQuotePricing?.rate.detailedPrice?.payNowTotal.fiat.value ??
      (selectedListing.availability.rate?.price.total.fiat.value || 0);

    const sortedOffers = offersResponse.rankedApplicableOffers.sort(
      (prev, current) => {
        const prevAmount = prev.percentage
          ? Math.floor(
              (prev.percentage * totalFiatValue + Number.EPSILON) * 100
            ) / 100
          : prev.amount.amount * -1;
        const currAmount = current.percentage
          ? Math.floor(
              (current.percentage * totalFiatValue + Number.EPSILON) * 100
            ) / 100
          : current.amount.amount * -1;
        return currAmount - prevAmount;
      }
    );

    const summaryResponse: WalletSummaryResponse =
      yield fetchTravelWalletSummary();

    yield put(setTravelWalletOffers(sortedOffers));
    yield put(setBestOfferOverall(sortedOffers[0]));
    yield put(setTravelWalletCredit(summaryResponse.credit));
    yield put(
      walletActions.setTravelWalletCredit(summaryResponse.creditBreakdown)
    );
    yield put(setFetchApplicableTravelWalletItemsCallStateSuccess());
  } catch (e) {
    yield put(setFetchApplicableTravelWalletItemsCallStateFailure());
  }
}
